import {
    FeatureType,
    GetEmailCaptureResponse,
    GetPopUpResponse,
    PopUpAnalytics
} from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { getLegacyEmailCaptures, getOrganizationPopups, getPopupAnalytics, getPopupEmailCaptures } from '../../../helpers/api/popups-api';

import { CreatePopUpComponent } from '../../create-pop-up-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import featuresInfo from '../../../helpers/state/features-info';
import organizationInfo from '../../../helpers/state/organization-info';
import { saveAs } from '../../../helpers/files';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-popup-addon')
export class AdminPopupAddon extends LitElement {
    amplitude_callsite: string = 'Popup Settings Drawer';

	@query('#createPopUpComponent')
	public createPopUpComponent?: CreatePopUpComponent;

    @state()
    selected_tab: string = 'Pop Ups';

    @state()
    private popups: GetPopUpResponse[] = [];

    @state()
    private popupsLoading: boolean = false;

    @state()
    private popupsError: string = '';

    @state()
    private isPopupBeingEdited: boolean = false;

    @state()
    private analytics: PopUpAnalytics[] = [];

    @state()
    private analyticsLoading: boolean = false;

    @state()
    private analyticsError: string = '';

    @state()
    private focusedPopupId: string | null = null;

    @state()
    private analyticsSortField: keyof PopUpAnalytics | 'conversion_rate' = 'impression_count';

    @state()
    private analyticsSortDesc: boolean = true;

    @state()
    private analyticsShowDrafts: boolean = false;

    @state()
    private legacyEmails: GetEmailCaptureResponse[] = [];

    @state()
    private legacyEmailsLoading: boolean = false;

    @state()
    private legacyEmailsError: string = '';

    @state()
    private showPreview: boolean = false;

    @state()
    private previewPopup: any = null;

    options: string[] = ['Pop Ups', 'Analytics'];
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindOrganizationState = new StateController(this, organizationInfo);

    async firstUpdated() {
        await this.loadPopups();
    }

    private async loadPopups() {
        if (!organizationInfo.id?.$oid) {
            return;
        }
        this.popupsLoading = true;
        this.popupsError = '';
        try {
            if (!topicData.topic?._id) {
                this.popups = [];
            } else {
                this.popups = (await getOrganizationPopups('admin-popup-addon', topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey)).filter(
                    popup => popup.topic_ids.some(id => id.$oid === topicData.topic?._id.$oid),
                );
            }
        } catch {
            this.popupsError = 'Failed to load popups';
        } finally {
            this.popupsLoading = false;
        }
    }

    openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.LeadsAcrossMaps
                }
            })
        );
    }

    handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];
        if (this.selected_tab === 'Analytics') {
            void this.loadAnalytics();
        }
    }


    render() {
        if (!topicData.isReady) {
            return html`<loading-element loadingString="Getting Pop Up Data..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                        <form-button
                            size="md"
                            variant="text"
                            icon="arrow_back"
                            @click=${() => this.dispatchEvent(new CustomEvent('close-fragment'))}
                        >Back</form-button>
                    </div>
                    <div class="middle-header-content">
                        Pop Up Feature
                    </div>

                    <div class="right-header-content">
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.options.indexOf(this.selected_tab)}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'Pop Ups' ? this.renderPopUpSettings() : ''}
                    ${this.selected_tab === 'Analytics' ? this.renderAnalytics() : ''}
                </div>
            </div>
        `;
    }

	async setEditedPopUp(e: CustomEvent) {
        const message = e.detail.popup;
        this.isPopupBeingEdited = true;
        if (this.createPopUpComponent) {
            await this.createPopUpComponent.setExistingPopUp(message);
        }
    }

    private handleCreatePopUpClose() {
        this.isPopupBeingEdited = false;
        this.showPreview = false;
        this.previewPopup = null;
    }

    private handleViewAnalytics(e: CustomEvent) {
        this.focusedPopupId = e.detail.popup_id;
        this.selected_tab = 'Analytics';
        void this.loadAnalytics();
    }

    private async loadAnalytics() {
        if (!organizationInfo.id?.$oid) {
            return;
        }
        this.analyticsLoading = true;
        this.analyticsError = '';
        try {
            this.analytics = await getPopupAnalytics(this.amplitude_callsite, topicData.topic?.organization_id?.$oid ?? '', topicData.topicId, topicData.topicKey);
        } catch {
            this.analyticsError = 'Failed to load analytics';
        } finally {
            this.analyticsLoading = false;
        }
        void this.loadLegacyEmails();
    }

    private async loadLegacyEmails() {
        if (!organizationInfo.id?.$oid) {
            return;
        }
        this.legacyEmailsLoading = true;
        this.legacyEmailsError = '';
        try {
            this.legacyEmails = await getLegacyEmailCaptures(this.amplitude_callsite, topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch {
            this.legacyEmailsError = 'Failed to load legacy email captures';
        } finally {
            this.legacyEmailsLoading = false;
        }
    }

    private downloadLegacyEmailsAsCSV() {
        if (this.legacyEmails.length === 0) return;

        const headers = ['Email', 'Name', 'Date', 'IP Address'];
        const rows = this.legacyEmails.map(email => [
            email.email ?? '',
            email.name ?? '',
            new Date(email.timestamp).toLocaleString(),
            email.ip_address ?? ''
        ]);

        const csvContent = [
            ['Legacy Email Captures (Pre-Popup)'],
            ['Export Date:', new Date().toLocaleString()],
            ['Total:', String(this.legacyEmails.length)],
            [],
            headers,
            ...rows
        ].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const filename = `legacy-email-captures-${new Date().toISOString().split('T')[0]}.csv`;
        saveAs(blob, filename);
    }

    private async downloadEmailsAsCSV(analytics: PopUpAnalytics) {
        try {
            const organizationId = topicData.topic?.organization_id?.$oid ?? '';
            const popupEmails = await getPopupEmailCaptures(this.amplitude_callsite, organizationId, analytics.popup_id.$oid);

            if (popupEmails.length === 0) {
                alert('No email captures for this popup');
                return;
            }

            // Generate CSV
            const csv = this.generateCSV(analytics.name, popupEmails);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const filename = `${analytics.name}-emails-${new Date().toISOString().split('T')[0]}.csv`;
            saveAs(blob, filename);
        } catch {
            alert('Failed to download emails');
        }
    }

    private generateCSV(popupName: string, emails: any[]): string {
        const headers = ['Email', 'Name', 'Date', 'IP Address'];
        const rows = emails.map(email => [
            email.email ?? '',
            email.name ?? '',
            new Date(email.timestamp).toLocaleString(),
            email.ip_address ?? ''
        ]);

        const csvContent = [
            ['Popup:', popupName],
            ['Export Date:', new Date().toLocaleString()],
            [],
            headers,
            ...rows
        ].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        return csvContent;
    }

    private togglePreview() {
        if (this.createPopUpComponent) {
            this.createPopUpComponent.togglePreviewPanel();
            if (this.createPopUpComponent.popup) {
                this.previewPopup = { ...this.createPopUpComponent.popup };
            }
        }
    }

    private handlePreviewToggled(e: CustomEvent) {
        this.showPreview = e.detail.previewMode;
    }

    private handlePopupUpdated(e: CustomEvent) {
        this.previewPopup = { ...e.detail.popup };
    }

    private handleExistingPopupPreview(e: CustomEvent) {
        this.previewPopup = { ...e.detail.popup };
        this.showPreview = true;
    }

    renderPopUpSettings() {
        return html`
            <div class="popup-settings-layout">
                <div class="popup-settings-cards">
                    <settings-card header="Create Pop Up" maxWidth="${this.showPreview ? '100%' : '50%'}">
                        ${this.previewPopup != null ? html`
                            <form-button
                                slot="header"
                                size="sm"
                                variant="outline"
                                @click=${() => this.togglePreview()}
                            >${this.showPreview ? 'Hide Preview' : 'Preview'}</form-button>
                        ` : ''}
                        <create-pop-up-component
                            .featureEnabled=${featuresInfo.leadsAcrossAllMapsEnabled}
                            .popup_target_id=${topicData.topic?._id}
                            .organizationId=${topicData.topic?.organization_id?.$oid ?? ''}
                            @popup-changed=${async () => await this.loadPopups()}
                            @close=${() => this.handleCreatePopUpClose()}
                            @preview-toggled=${this.handlePreviewToggled}
                            @popup-updated=${this.handlePopupUpdated}
                            id="createPopUpComponent"
                        ></create-pop-up-component>
                    </settings-card>
                    <settings-card header="Existing Pop Ups" maxWidth="${this.showPreview ? '100%' : '50%'}">
                        <existing-pop-ups-component
                            .organizationId=${topicData.topic?.organization_id?.$oid ?? ''}
                            .popups=${this.popups}
                            .loading=${this.popupsLoading}
                            .error=${this.popupsError}
                            .isPopupBeingEdited=${this.isPopupBeingEdited}
                            id="existingPopUpsComponent"
                            @set-edited-popup=${this.setEditedPopUp}
                            @preview-popup=${this.handleExistingPopupPreview}
                            @view-popup-analytics=${this.handleViewAnalytics}
                        ></existing-pop-ups-component>
                    </settings-card>
                </div>
                ${this.showPreview && this.previewPopup != null ? html`
                    <div class="preview-panel">
                        <div class="preview-panel__header">
                            <h3>Preview</h3>
                            <mwc-icon style="cursor:pointer" @click=${() => this.togglePreview()}>close</mwc-icon>
                        </div>
                        <div class="preview-panel__content">
                            <div class="preview-popup-wrapper" style="background-color: ${this.previewPopup?.background_color ?? '#FFF'}">
                                ${(this.previewPopup?.header_logo_url as string) ? html`
                                    <div class="preview-cover-image-container">
                                        <img src="${this.previewPopup.header_logo_url}" class="preview-cover-image" />
                                    </div>
                                ` : ''}
                                <email-capture-modal
                                    .popUp=${this.previewPopup}
                                    .userLat=${(() => { const gt = (this.previewPopup as GetPopUpResponse)?.geo_trigger; return gt ? Number(gt.lat) + 0.003 : undefined; })()}
                                    .userLng=${(() => { const gt = (this.previewPopup as GetPopUpResponse)?.geo_trigger; return gt ? Number(gt.lng) + 0.002 : undefined; })()}
                                    @close-modal=${() => this.togglePreview()}
                                ></email-capture-modal>
                            </div>
                        </div>
                    </div>
                ` : ''}
            </div>
        `;
    }

    private getConversionRate(row: PopUpAnalytics): number {
        return row.impression_count > 0 ? (row.email_capture_count / row.impression_count) * 100 : 0;
    }

    private getSortedFilteredAnalytics(): PopUpAnalytics[] {
        let filtered = this.analytics;

        if (!this.analyticsShowDrafts) {
            filtered = filtered.filter(row => {
                const popup = this.popups.find(p => p._id.$oid === row.popup_id.$oid);
                return !popup || popup.is_active;
            });
        }

        const sorted = [...filtered].sort((a, b) => {
            let aVal: number;
            let bVal: number;
            if (this.analyticsSortField === 'conversion_rate') {
                aVal = this.getConversionRate(a);
                bVal = this.getConversionRate(b);
            } else {
                aVal = a[this.analyticsSortField] as number;
                bVal = b[this.analyticsSortField] as number;
            }
            return this.analyticsSortDesc ? bVal - aVal : aVal - bVal;
        });

        return sorted;
    }

    private handleSortClick(field: keyof PopUpAnalytics | 'conversion_rate') {
        if (this.analyticsSortField === field) {
            this.analyticsSortDesc = !this.analyticsSortDesc;
        } else {
            this.analyticsSortField = field;
            this.analyticsSortDesc = true;
        }
    }

    private renderSortArrow(field: keyof PopUpAnalytics | 'conversion_rate') {
        if (this.analyticsSortField !== field) return '';
        return this.analyticsSortDesc ? ' ▼' : ' ▲';
    }

    renderAnalytics() {
        const rows = this.getSortedFilteredAnalytics();
        return html`
            <settings-card header="Popup Performance">
                ${this.analyticsLoading ? html`<loading-element loadingString="Loading analytics..."></loading-element>` : ''}
                ${this.analyticsError ? html`<div class="error">${this.analyticsError}</div>` : ''}
                ${!this.analyticsLoading && this.analytics.length === 0
                    ? html`<div class="empty-analytics">No analytics data yet. Data will appear once popups have been viewed.</div>`
                    : html`
                        <div class="analytics-filter-row">
                            <label class="analytics-filter-toggle">
                                <input type="checkbox" .checked=${this.analyticsShowDrafts} @change=${(e: Event) => { this.analyticsShowDrafts = (e.target as HTMLInputElement).checked; }} />
                                Show drafts
                            </label>
                        </div>
                        <div class="analytics-table">
                            <div class="analytics-header-row">
                                <div class="analytics-cell name-cell">Popup Name</div>
                                <div class="analytics-cell stat-cell sortable-header" @click=${() => this.handleSortClick('impression_count')}>Views${this.renderSortArrow('impression_count')}</div>
                                <div class="analytics-cell stat-cell sortable-header" @click=${() => this.handleSortClick('click_count')}>Clicks${this.renderSortArrow('click_count')}</div>
                                <div class="analytics-cell stat-cell sortable-header" @click=${() => this.handleSortClick('dismiss_count')}>Dismissed${this.renderSortArrow('dismiss_count')}</div>
                                <div class="analytics-cell stat-cell sortable-header" @click=${() => this.handleSortClick('email_capture_count')}>Emails${this.renderSortArrow('email_capture_count')}</div>
                                <div class="analytics-cell stat-cell sortable-header" @click=${() => this.handleSortClick('conversion_rate')}>Conv. Rate${this.renderSortArrow('conversion_rate')}</div>
                                <div class="analytics-cell action-cell">Actions</div>
                            </div>
                            ${rows.map(
                                row => {
                                    const popup = this.popups.find(p => p._id.$oid === row.popup_id.$oid);
                                    const isDraft = popup !== undefined && !popup.is_active;
                                    return html`
                                    <div class="analytics-row ${this.focusedPopupId === row.popup_id.$oid ? 'focused' : ''} ${isDraft ? 'draft' : ''}">
                                        <div class="analytics-cell name-cell">
                                            ${row.name}
                                            ${isDraft ? html`<span class="draft-badge">Draft</span>` : ''}
                                        </div>
                                        <div class="analytics-cell stat-cell">${row.impression_count}</div>
                                        <div class="analytics-cell stat-cell">${row.click_count}</div>
                                        <div class="analytics-cell stat-cell">${row.dismiss_count}</div>
                                        <div class="analytics-cell stat-cell">${row.email_capture_count}</div>
                                        <div class="analytics-cell stat-cell">
                                            ${row.impression_count > 0
                                                ? `${Math.round(this.getConversionRate(row))}%`
                                                : '—'}
                                        </div>
                                        <div class="analytics-cell action-cell">
                                            ${row.email_capture_count > 0
                                                ? html`<button class="download-btn" @click=${async () => await this.downloadEmailsAsCSV(row)}>
                                                        <mwc-icon>download</mwc-icon>
                                                    </button>`
                                                : html`<span class="no-data">—</span>`}
                                        </div>
                                    </div>
                                `;
                            })}
                        </div>
                    `}
            </settings-card>
            <settings-card header="Legacy Email Captures">
                <div class="legacy-description">Email captures collected before the new popup experience went live across all maps.</div>
                ${this.legacyEmailsLoading ? html`<loading-element loadingString="Loading legacy emails..."></loading-element>` : ''}
                ${this.legacyEmailsError ? html`<div class="error">${this.legacyEmailsError}</div>` : ''}
                ${!this.legacyEmailsLoading && this.legacyEmails.length === 0 && !this.legacyEmailsError
                    ? html`<div class="empty-analytics">No legacy email captures found.</div>`
                    : ''}
                ${!this.legacyEmailsLoading && this.legacyEmails.length > 0
                    ? html`
                        <div class="legacy-summary">
                            <span><strong>${this.legacyEmails.length}</strong> email capture${this.legacyEmails.length !== 1 ? 's' : ''}</span>
                            <button class="download-btn" @click=${() => this.downloadLegacyEmailsAsCSV()}>
                                <mwc-icon>download</mwc-icon> Download CSV
                            </button>
                        </div>
                    `
                    : ''}
            </settings-card>
        `;
    }

    static get styles() {
        return css`

            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
                flex-wrap: wrap;
            }

            .checkboxHolder {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .right-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
                flex-wrap: wrap;
            }
            .modalContent {
                position: relative;
                width: 100%;
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .general-stats-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
            }

            h4.sub-title {
                font-weight: 600;
                font-size: 12px;
                line-height: 100%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            #holder {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            #container {
                flex-direction: column;
                overflow: hidden auto;
                padding-right: 20px;
                box-sizing: border-box;
                flex: 1;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
                margin-top: 5px;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .green {
                --mdc-theme-primary: var(--main-success-color);
            }

            .gray {
                --mdc-theme-primary: var(--main-disabled-color);
            }

            .save-actions {
                display: flex;
                justify-content: flex-end;
                margin: 0.5rem;
            }

            .visibility-square {
                display: flex;
                margin-left: 0.5rem;
                color: var(--theme-input-color);
                border: 1px solid var(--theme-input-color);
                border-radius: 5px;
                padding: 0.1rem 0.25rem;
                align-items: center;
                font-size: 0.8rem;
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }

            .individual-participant-drawer {
                border-right: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                background-color: var(--theme-white-color);
                padding: 1rem;
                transition: width 0.3s;
                display: flex;
                flex-direction: column;
                display: flex;
                overflow-y: auto;
            }

            .individual-participant-drawer.open {
                width: 30%;
            }

            .individual-participant-drawer.closed {
                width: 0;
                padding: 0;
            }

            .helpSubHeader {
                display: inline-flex;
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
                align-items: center;
            }

            .summary {
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            .summary-section {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-input-color);
                pointer-events: none;
            }

            .move {
                margin-left: 1rem;
            }

            .summary mwc-icon {
                margin-right: 0.5rem;
                --mdc-icon-size: 20px;
            }

            .move-icon {
                cursor: pointer;
                margin: 0;
            }

            .move-icon:hover {
                color: var(--main-primary-color);
            }

            .existingRules {
                display: flex;
                flex-direction: column;
                --mdc-icon-size: 16px;
            }

            .existingRules mwc-icon {
                cursor: pointer;
                margin-right: 0.5rem;
                margin-left: 0.5rem;
            }

            .ruleName {
                margin: 0;
                font-size: 0.8rem;
                text-align: center;
                flex: 1;
                border: 1px solid var(--main-secondary-color);
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .rule {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid var(--main-primary-color);
                padding: 0.5rem;
            }

            .operator-holder {
                display: flex;
                justify-content: center;
                flex-direction:row;

            }

            .operator {
                text-align: center;
                font-size: 0.6rem;
                font-weight: bold;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                color: white;
                border-radius: 5px;
                padding: 0 0.5rem;
                --mdc-icon-size: 12px;
            }

            .operator mwc-icon {
                margin-left: 0;
                margin-right: 0.25rem;

            }

            .and {
                background-color: var(--main-primary-color);
                color : white;
            }

            .or {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-or {
                cursor: pointer;
                color: var(--main-secondary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-or:hover {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-and {
                cursor: pointer;
                color: var(--main-primary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-and:hover {
                background-color: var(--main-primary-color);
                color: white;
            }

            .visibilityRules {
                display: flex;
                flex-direction: column;
                margin-top: 1rem;
            }

            .helpSubeHeaderUnderline {
                margin: 0px;
                font-weight: normal;
                font-size: 0.8rem;
                flex: 1;
                text-decoration: underline;
                color: var(--theme-primary-color);
                display: inline-flex;
            }

            form-tooltip {
                font-size: 0.8rem;
            }

            .archiveField {
                cursor: pointer;
                display: flex;
                align-items: flex-end;
                justify-content: flex-end;
                text-decoration: underline;
                color: var(--main-invalid-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .input {
                width: 100%;
                padding: auto 5px;
                box-sizing: border-box;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            .ruleSelectorWrapper {
                display: flex;
                margin-top: 1rem;
                flex-direction: row;
                flex-wrap: wrap;
                align-content: center;
                justify-content: center;
                gap: 3px 5px;
            }

            .gatedSection {
                cursor: pointer;
            }

            .description {
                color: var(--theme-input-color);
            }

            .add-new-rule-wrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 5px;
            }

            .customizedFields__disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .hidden {
                display: none;
            }

            .ruleSubmissionButtons {
                display: flex;
                justify-content: center;
                margin-top: 5px;
                padding-bottom: 10px;
                gap: 5px;
            }

            .analytics-table {
                display: flex;
                flex-direction: column;
                width: 100%;
                font-size: 12px;
            }

            .analytics-filter-row {
                display: flex;
                justify-content: flex-end;
                margin-bottom: 8px;
            }

            .analytics-filter-toggle {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: var(--theme-input-color);
                cursor: pointer;
            }

            .analytics-header-row {
                display: flex;
                font-weight: 700;
                background: var(--theme-border-color);
                padding: 8px 0;
            }

            .sortable-header {
                cursor: pointer;
                user-select: none;
            }

            .sortable-header:hover {
                color: var(--main-primary-color);
            }

            .analytics-row {
                display: flex;
                padding: 8px 0;
                border-bottom: 1px solid #DADADA;
            }

            .analytics-row.focused {
                background: #e8f4fd;
                border-left: 3px solid var(--main-primary-color);
            }

            .analytics-row.draft {
                opacity: 0.6;
            }

            .draft-badge {
                display: inline-block;
                font-size: 10px;
                font-weight: 600;
                color: var(--theme-input-color);
                background: var(--theme-border-color);
                border-radius: 3px;
                padding: 1px 6px;
                margin-left: 6px;
                vertical-align: middle;
            }

            .analytics-cell {
                padding: 0 10px;
                display: flex;
                align-items: center;
            }

            .analytics-cell.name-cell {
                flex: 2;
                font-weight: 500;
            }

            .analytics-cell.stat-cell {
                flex: 1;
                justify-content: center;
            }

            .analytics-cell.action-cell {
                flex: 1;
                justify-content: center;
            }

            .download-btn {
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 6px 12px;
                background-color: var(--main-secondary-color);
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
            }

            .download-btn:hover {
                opacity: 0.9;
            }

            .download-btn mwc-icon {
                font-size: 16px;
            }

            .no-data {
                color: #6c757d;
            }

            .empty-analytics {
                text-align: center;
                padding: 40px;
                color: #6c757d;
            }

            .legacy-description {
                color: var(--theme-input-color);
                font-size: 12px;
                margin-bottom: 12px;
            }

            .legacy-summary {
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-size: 13px;
            }

            .error {
                background-color: #f8d7da;
                color: #721c24;
                padding: 12px;
                border-radius: 4px;
                margin-bottom: 20px;
            }

            .popup-settings-layout {
                display: flex;
                gap: 20px;
                align-items: flex-start;
                align-self: flex-start;
                width: 100%;
            }

            .popup-settings-cards {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 20px;
                min-width: 0;
            }

            .popup-settings-cards settings-card {
                width: 100%;
            }


            .preview-panel {
                flex: 0 0 340px;
                border: 1px solid var(--theme-border-color);
                border-radius: 0.5rem;
                background-color: var(--theme-white-color);
                box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.05),
                    0px 2px 2px -2px rgba(31, 27, 45, 0.08);
                padding: 1rem;
                position: sticky;
                top: 0;
                max-height: calc(100vh - 200px);
                display: flex;
                flex-direction: column;
            }

            .preview-panel__header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;
            }

            .preview-panel__header h3 {
                margin: 0;
                font-size: 0.9rem;
                font-weight: 700;
            }

            .preview-panel__content {
                flex: 1;
                overflow-y: auto;
                display: flex;
                align-items: flex-start;
                justify-content: center;
                background-color: #f5f5f5;
                border-radius: 8px;
            }

            .preview-panel__content email-capture-modal {
                width: 100%;
            }

            .preview-popup-wrapper {
                width: 100%;
                border-radius: 8px;
                overflow: hidden;
            }

            .preview-cover-image-container {
                width: 100%;
                position: relative;
            }

            .preview-cover-image {
                width: 100%;
                max-height: 180px;
                object-fit: cover;
                display: block;
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }

                .popup-settings-layout {
                    flex-direction: column;
                }

                .preview-panel {
                    flex: none;
                    position: static;
                    max-height: none;
                }
            }
        `;
    }
}
